// ── Game Types ── Shared types and constants for the Game class and its helper modules

import * as THREE from 'three';
import { type GameLoop } from './core/loop';
import { type GameClock } from './core/clock';
import { type FPSCamera } from './renderer/camera';
import { type WeaponViewmodel } from './renderer/viewmodel';
import { type WeaponState } from './gameplay/weapon';
import { type LocalPlayer, type RemotePlayer } from './gameplay/player';
import { type Connection } from './net/connection';
import { type PredictionSystem } from './net/prediction';
import { type EntityInterpolator } from './net/interpolation';
import { type HUD } from './renderer/hud';
import { type Scoreboard } from './ui/screens/scoreboard';
import { type DeathScreen } from './ui/screens/death-screen';
import { type MainMenu } from './ui/screens/main-menu';
import { type DebugDisplay } from './utils/debug';
import { type Minimap } from './ui/minimap';
import { type DamageNumberSystem } from './ui/damage-numbers';
import { type ScopeOverlay } from './ui/scope-overlay';
import { type DirectionalIndicator } from './ui/directional-indicator';
import { type DamageIndicatorSystem } from './ui/damage-indicator';
import { type BuyMenu } from './ui/buy-menu';
import { type NetGraph } from './ui/net-graph';
import { type RoundScore, type RoundPhase } from './ui/round-score';
import { type PostProcessing } from './renderer/postprocessing';
import { type AtmosphereSystem } from './renderer/atmosphere';
import { type SkinLoader } from './cosmetics/skin-loader';
import { type LoadingScreen } from './cosmetics/loading-screen';
import { type SkinSelector } from './ui/skin-selector';
import { type SettingsMenu } from './ui/settings-menu';
import { type SpectatorSystem } from './spectator/spectator';
import { type SpectatorHUD } from './spectator/spectator-hud';
import { type XRayOverlay } from './spectator/spectator-xray';
import { type AutoDirector } from './spectator/auto-director';
import { type RagdollRenderer } from './renderer/ragdoll-renderer';
import { type WeaponDropManager } from './gameplay/weapon-pickup';
import { type TeamSelectMenu } from './ui/team-select';
import { type RegistrationDialog } from './ui/registration-dialog';
import { type LeaderboardPanel } from './ui/leaderboard-panel';
import { type GrenadeInventory } from './gameplay/grenade';
import { type GrenadeProjectileManager } from './gameplay/grenade-projectile';
import { type SmokeCloudHandle, type FireZoneHandle } from './renderer/grenade-effects';
import { type GrenadeId } from './core/config';
import { type BombEntity } from './gameplay/bomb';
import { type ChatUI } from './ui/chat';
import { type VolumetricSmokeSystem } from './renderer/volumetric-smoke';
import { type WeaponSkinLoader } from './cosmetics/weapon-skin-loader';
import { type WeaponLoadout } from './cosmetics/weapon-skin-types';
import { type DeathReplaySystem } from './ui/death-replay';
import { type ReplayRecorder } from './replay/replay-recorder';
import { type ReplayMenu } from './ui/replay-menu';
import { type KillEffect } from './ui/kill-effect';
import { type HeadshotIndicator } from './ui/headshot-indicator';
import { type PingWheel } from './ui/ping-wheel';
import { type VoiceIndicators } from './ui/voice-indicators';
import { type AimAssist } from './input/aim-assist';
import { type HapticFeedback } from './input/haptics';
import { type TouchInputProvider } from './input/touch-input-provider';
import { type RotateOverlay } from './ui/rotate-overlay';
import { type MobileTutorial } from './ui/mobile-tutorial';
import { type HudLayoutEditor } from './ui/hud-layout-editor';
import { type QuickBuyController } from './ui/quick-buy';
import { type SkinMarketUI } from './ui/skin-market';
import { type SkinPreviewRenderer } from './ui/skin-preview';
import { type VotePanel } from './ui/vote-panel';
import { type OverwatchPanel } from './ui/overwatch-panel';
import { type ProgressionPanel } from './ui/progression-panel';
import { type RecoilKickSystem } from './renderer/recoil-kick';
import { type InspectAnimationSystem } from './renderer/inspect-animation';
import { type LoadoutSlots } from './gameplay/loadout';

export type GameState = 'menu' | 'connecting' | 'playing' | 'dead' | 'spectating' | 'team_select' | 'replay';

// Scope zoom FOV
export const NORMAL_FOV = 90;
export const SCOPE_FOV = 30;

/**
 * Shared context that helper modules use to access and mutate Game state.
 * The Game class implements this interface and passes `this` to helper functions.
 */
export interface GameContext {
  // Core systems
  loop: GameLoop;
  clock: GameClock;
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  camera: FPSCamera;

  // Gameplay
  localPlayer: LocalPlayer;
  remotePlayers: Map<number, RemotePlayer>;
  weaponState: WeaponState;
  /**
   * Slot table for weapon keybinds 1/2/3 (primary/secondary/melee). Updated
   * on buy / pickup / respawn / round_start / arms_race_advance so pressing
   * a slot key switches to the *currently-owned* weapon in that slot rather
   * than a hardcoded default.
   */
  loadoutSlots: LoadoutSlots;
  viewmodel: WeaponViewmodel;

  // Networking
  connection: Connection;
  prediction: PredictionSystem;
  interpolator: EntityInterpolator;

  // UI
  hud: HUD;
  scoreboard: Scoreboard;
  deathScreen: DeathScreen;
  mainMenu: MainMenu;
  debug: DebugDisplay;

  // New UI systems
  minimap: Minimap;
  damageNumbers: DamageNumberSystem;
  scopeOverlay: ScopeOverlay;
  directionalIndicator: DirectionalIndicator;
  damageIndicator: DamageIndicatorSystem;
  buyMenu: BuyMenu;
  netGraph: NetGraph;
  roundScore: RoundScore;

  // Graphics systems
  postProcessing: PostProcessing;
  atmosphere: AtmosphereSystem;
  puddleSystem: any;


  // Skin system
  skinLoader: SkinLoader;
  loadingScreen: LoadingScreen;
  skinSelector: SkinSelector;
  localPlayerBody: THREE.Group | null;
  /** G6: whether the C4 TPP prop is currently attached to localPlayerBody. */
  localTppBombAttached: boolean;

  // Weapon skin system
  weaponSkinLoader: WeaponSkinLoader;
  weaponLoadout: WeaponLoadout;

  // Marketplace & skin preview (optional — degrade gracefully when unavailable)
  skinMarket?: SkinMarketUI;
  skinPreview?: SkinPreviewRenderer;

  // Settings & perspective
  settingsMenu: SettingsMenu;

  // Team selection
  teamSelectMenu: TeamSelectMenu;

  // Registration & Leaderboard
  registrationDialog: RegistrationDialog;
  leaderboardPanel: LeaderboardPanel;

  // Spectator
  spectator: SpectatorSystem;
  spectatorHud: SpectatorHUD;
  xrayOverlay: XRayOverlay;
  autoDirector: AutoDirector;

  // Ragdoll & weapon drop rendering
  ragdollRenderer: RagdollRenderer;
  weaponDropMgr: WeaponDropManager;

  // State
  state: GameState;
  serverUrl: string;
  footstepTimer: number;
  money: number;
  isScoped: boolean;
  shakeOffset: { x: number; y: number };

  // Killcam state
  killcamActive: boolean;
  killcamTarget: RemotePlayer | null;
  killcamTimer: number;
  readonly KILLCAM_DURATION: number;

  // Round state
  tScore: number;
  ctScore: number;
  roundNum: number;
  roundPhase: RoundPhase;

  // F-NC-003 — Reconnect token issued by the server in `welcome.data
  // .reconnectToken`. The client echoes it back as `joinData.reconnectToken`
  // on the next reconnect so the server can restore the saved PlayerState
  // (position, kills, money, weapon) instead of falling through to a fresh
  // join. May be null when the server does not issue a token (e.g. test
  // contexts) or before the first welcome arrives.
  reconnectToken: string | null;

  // Wallbang indicator
  wallbangTimer: number;
  wallbangEl: HTMLElement | null;

  // Death-to-spectator transition timer (seconds remaining before entering spectator)
  deathToSpectatorTimer: number;

  // Bomb 3D entity
  bombEntity: BombEntity;

  // Chat system
  chatUI: ChatUI;

  // Grenade system
  grenadeInventory: GrenadeInventory;
  grenadeProjectiles: GrenadeProjectileManager;
  activeSmokeZones: SmokeCloudHandle[];
  activeFireZones: FireZoneHandle[];
  flashBlindTimer: number;
  selectedGrenade: GrenadeId | null;
  previousWeaponBeforeGrenade: string | null;
  grenadeThrowAnimTimer: number;

  // Volumetric smoke system
  volumetricSmoke: VolumetricSmokeSystem;

  // Replay systems
  deathReplay: DeathReplaySystem;
  replayRecorder: ReplayRecorder;
  replayMenu: ReplayMenu;

  // Kill/headshot UI effects
  killEffect: KillEffect;
  headshotIndicator: HeadshotIndicator;

  // Communication
  pingWheel: PingWheel | null;

  // Voice indicators
  voiceIndicators: VoiceIndicators;

  // Mobile-only systems (null on desktop)
  touchInput: TouchInputProvider | null;
  aimAssist: AimAssist | null;
  hapticFeedback: HapticFeedback | null;
  rotateOverlay: RotateOverlay | null;
  mobileTutorial: MobileTutorial | null;
  hudLayoutEditor: HudLayoutEditor | null;
  /** MOB-W6: customizable quick-buy buttons. Null on desktop. */
  quickBuy: QuickBuyController | null;

  // Vote-kick, Overwatch, Progression panels
  votePanel: VotePanel;
  overwatchPanel: OverwatchPanel;
  progressionPanel: ProgressionPanel;

  // Renderer overlays driven from input/fire paths
  recoilKick: RecoilKickSystem;
  inspectAnimation: InspectAnimationSystem;

  // Recent game events buffer for auto-director scoring
  recentEvents: Array<{ type: string; data: any }>;
}
